import type { Module } from '@/types/course';

export const module7: Module = {
  slug: 'modulo-7-monedas-no-fundir',
  number: 7,
  title: 'Monedas que NO debes fundir',
  description:
    'Aprende a identificar monedas de uso corriente, históricas y extranjeras cuyo valor numismático, legal o patrimonial supera con creces su contenido en metal. Fundirlas puede ser ilegal o destruir un valor muy superior.',
  durationMinutes: 70,
  badge: 'numismatico',
  lessons: [
    {
      slug: 'euros-moneda-curso-legal',
      number: '7.1',
      title: 'Euros: moneda de curso legal',
      durationMinutes: 15,
      videoUrl: null,
      content: `Las monedas de euro son las que más frecuentemente nos llegan con la pregunta: "¿Tienen valor en metal?" La respuesta corta: **no, y además destruirlas es ilegal**. Pero conocerlas bien evita confusiones cuando un cliente las mezcla con monedas de oro o plata reales.

## 1, 2 y 5 céntimos de euro

![1 céntimo de euro — anverso (catedral de Burgos)](/coins/euro-1c-anverso.png)
![1 céntimo de euro — reverso](/coins/euro-1c-reverso.png)

- **Material**: acero recubierto de cobre (no contienen cobre puro)
- **Anverso español**: catedral de Santiago de Compostela
- **Reverso común**: valor facial sobre globo terráqueo
- **Diámetros**: entre 16,25 mm (1c) y 21,25 mm (5c)
- **Valor intrínseco en metal**: prácticamente cero — el coste de fabricación supera su valor facial

**¿Se pueden fundir?** No. Son moneda de curso legal. Su destrucción intencionada puede constituir infracción administrativa. Además, el metal recuperado (acero con baño de cobre) no tiene mercado.

## 10, 20 y 50 céntimos de euro

![50 céntimos de euro — anverso (Cervantes)](/coins/euro-50c-cervantes-anverso.png)
![50 céntimos de euro — reverso](/coins/euro-50c-reverso.png)

- **Material**: oro nórdico (*Nordic Gold*: 89% Cu, 5% Al, 5% Zn, 1% Sn) — aspecto dorado pero **no contienen oro**
- **Anverso español**: busto de Miguel de Cervantes con pluma y gorguera
- **Reverso común**: valor sobre mapa de Europa
- **Dato clave**: el nombre "oro nórdico" confunde a muchos clientes. Es una aleación de cobre diseñada por la fábrica finlandesa Outokumpu específicamente para monedas, buscando un color dorado, resistencia al desgaste y propiedades anti-alérgicas

**¿Se pueden fundir?** No. Mismo caso: moneda de curso legal, sin valor metálico apreciable.

## 1 euro y 2 euros

![2 euros — anverso (Felipe VI)](/coins/euro-2e-felipeVI-anverso.png)
![2 euros — reverso](/coins/euro-2e-reverso.png)

- **Material**: bimetálicas (núcleo de níquel-latón + anillo de cuproníquel, o viceversa)
- **Anverso español**: efigie del Rey — **Juan Carlos I** (1999-2014), **Felipe VI** (desde 2015)
- **Reverso común**: valor sobre mapa de Europa
- **Peso**: 7,5 g (1€) y 8,5 g (2€)

**Caso especial — 2€ conmemorativos**: Mismo tamaño, peso y material que el 2€ ordinario, pero con diseños especiales (Patrimonio UNESCO, efemérides, etc.). Algunos coleccionistas pagan 3-10€ por piezas en estado perfecto, pero **siguen siendo moneda de curso legal** y su valor facial es siempre 2€.

## Resumen: ¿por qué NO fundir euros?

| Razón | Detalle |
|-------|---------|
| **Legal** | Son moneda de curso legal; destruirlas es infracción |
| **Económico** | El metal que contienen vale menos que su valor facial |
| **Práctico** | No hay refinería que acepte monedas de euro para fundición |

La regla es simple: **las monedas de euro se gastan, se ahorran o se cambian en el banco**. Nunca se funden.`,
      keyPoints: [
        'Las monedas de euro están hechas de acero, cobre, níquel y aleaciones — NO contienen oro ni plata.',
        '"Oro nórdico" es una aleación de cobre, no contiene oro real.',
        'Destruir moneda de curso legal es infracción administrativa.',
        'Los 2€ conmemorativos siguen siendo moneda de curso legal con valor de 2€.',
        'El valor intrínseco del metal es inferior al valor facial en todos los casos.',
      ],
    },
    {
      slug: 'pesetas-moneda-historica',
      number: '7.2',
      title: 'Pesetas: moneda histórica reciente',
      durationMinutes: 20,
      videoUrl: null,
      content: `La peseta dejó de ser moneda de curso legal el 28 de febrero de 2002, pero sigue siendo la moneda que más frecuentemente aparece en herencias, cajones olvidados y cajas fuertes familiares. Algunas piezas tienen un valor numismático que multiplica por 10, 50 o incluso 100 veces su peso en metal.

## Pesetas de Franco (1947-1975)

![100 pesetas Franco 1966 — anverso](/coins/peseta-100ptas-franco-anverso.png)
![100 pesetas Franco 1966 — reverso](/coins/peseta-100ptas-franco-reverso.png)

- **Anverso**: retrato de Francisco Franco con la leyenda "FRANCISCO FRANCO CAUDILLO DE ESPAÑA POR LA G. DE DIOS"
- **Reverso**: águila imperial con escudo
- **Material**: cuproníquel (las comunes), plata 800‰ (las de 100 ptas de 1966)
- **Truco de las estrellas**: la fecha real de acuñación NO es la que aparece bajo el busto, sino la que está dentro de las estrellas de seis puntas del reverso. Una moneda con "1966" en el busto pero estrella "19-69" vale significativamente más
- **Valor**: las piezas comunes de 1, 5, 25 y 50 pesetas tienen valor numismático bajo (0,50-5€). Las de 100 pesetas de plata (1966, estrellas 66, 67, 68, 69) pueden valer entre 8€ y 150€ según la estrella y conservación

## Pesetas de Juan Carlos I (1975-2001)

![500 pesetas JCI — anverso](/coins/peseta-500ptas-jci-anverso.png)
![500 pesetas JCI — reverso](/coins/peseta-500ptas-jci-reverso.png)

- **Anverso**: retrato del Rey Juan Carlos I (evoluciona con los años)
- **Reverso**: diversos diseños conmemorativos (Mundial 82, Expo 92, autonomías, etc.)
- **Material**: cuproníquel y aluminio-bronce; las de 500 pesetas, bimetálicas
- **Valor numismático**: la mayoría tienen valor bajo (0,20-3€). Las **excepciones** más buscadas:
  - 50 pesetas 1984 (variante sin "M" de ceca): hasta 60€
  - Carteras oficiales FNMT completas: 20-200€ según año
  - Sets conmemorativos Barcelona 92 en plata: 30-150€

## Duros de plata — Alfonso XII y Alfonso XIII

![5 pesetas plata Alfonso XII 1878 — anverso](/coins/peseta-duro-alfonsoXII-anverso.png)
![5 pesetas plata Alfonso XII 1878 — reverso](/coins/peseta-duro-alfonsoXII-reverso.png)

Aquí empieza lo importante: **el "duro" de plata** es probablemente la moneda más emblemática de la numismática española.

- **Denominación**: 5 pesetas
- **Material**: **plata 900‰** (90% plata pura)
- **Peso**: 25 gramos (22,5 g de plata fina)
- **Diámetro**: 37 mm — piezas grandes y pesadas
- **Reyes**: Alfonso XII (1875-1885) y Alfonso XIII (1886-1931)

**Valor del metal vs valor numismático**:

| Concepto | Valor aproximado |
|----------|-----------------|
| Plata contenida (22,5 g × ~0,90€/g) | ~20€ |
| Valor numismático (pieza común, BC) | 30-60€ |
| Valor numismático (conservación EBC) | 80-300€ |
| Rarezas (años escasos, variantes) | 500-5.000€+ |

**Fundirlo destruiría entre 10€ y 4.980€ de valor**. Es el ejemplo perfecto de por qué nunca se debe fundir sin consultar a un experto.

## Céntimos de la II República (1937)

![Peseta República 1937 — anverso](/coins/peseta-republica-1937-anverso.png)
![Peseta República 1937 — reverso](/coins/peseta-republica-1937-reverso.png)

- **Anverso**: Hispania con gorro frigio, leyenda "REPÚBLICA ESPAÑOLA"
- **Material**: cobre rojizo o latón
- **Contexto**: acuñadas durante la Guerra Civil, a menudo con defectos de producción
- **Valor**: el contenido metálico es insignificante (cobre), pero el valor numismático puede ser **muy alto** — piezas en buen estado alcanzan 15-100€, y variantes raras mucho más

## Resumen: ¿por qué NO fundir pesetas?

1. **Ya no tienen valor facial**: no puedes gastarlas, pero el Banco de España ya no las canjea (plazo cerrado desde 2020)
2. **Muchas tienen valor numismático**: especialmente los duros de plata, las de 100 pesetas en plata y las de la República
3. **La plata contenida vale poco**: incluso en los duros, el valor de la plata es inferior al valor numismático en la inmensa mayoría de casos
4. **Consulta antes de fundir**: un experto puede identificar en segundos si tu peseta vale 1€ o 500€`,
      keyPoints: [
        'Las pesetas dejaron de ser moneda de curso legal en 2002 y ya no se pueden canjear en el Banco de España.',
        'El "duro" de plata (5 pesetas Alfonso XII/XIII) contiene 22,5g de plata fina pero su valor numismático suele ser muy superior.',
        'La fecha real de las pesetas de Franco está en las estrellas, no bajo el busto.',
        'Las monedas de la II República (1937) pueden tener alto valor coleccionista pese a ser de cobre.',
        'REGLA: nunca fundir pesetas sin consultar su valor numismático primero.',
      ],
    },
    {
      slug: 'moneda-historica-patrimonio',
      number: '7.3',
      title: 'Moneda histórica: patrimonio protegido por ley',
      durationMinutes: 20,
      videoUrl: null,
      content: `Cuando una moneda supera los 100 años de antigüedad, entra en una categoría especial en España: puede estar protegida por la **Ley 16/1985 del Patrimonio Histórico Español**. Su destrucción no solo destruye valor — puede ser **delito** castigado con pena de prisión (art. 323 del Código Penal).

## Marco legal

> **Art. 323 CP**: "Será castigado con la pena de prisión de seis meses a tres años o multa de doce a veinticuatro meses el que cause daños en bienes de valor histórico, artístico, científico, cultural o monumental."

Esto aplica a monedas que forman parte del patrimonio histórico español. **Fundirlas es un daño irreversible** equiparable a destruir una pieza de museo.

## Isabel II (1833-1868)

![Isabel II — anverso](/coins/historica-isabelII-anverso.png)
![Isabel II — reverso](/coins/historica-isabelII-reverso.png)

- **Identificación**: busto femenino joven con diadema. Leyenda "ISABEL 2ª POR LA G. DE DIOS"
- **Reverso**: escudo coronado de España
- **Material**: oro, plata o cobre según denominación
- **Denominaciones**: desde 1 céntimo de escudo hasta 100 reales en oro
- **Las piezas de oro** (100 reales, 10 escudos) contienen oro fino pero su valor numismático puede ser 2-5 veces superior al valor del metal
- **Las piezas de plata** (4 reales, 20 reales) tienen valor numismático moderado a alto

## Gobierno Provisional (1868-1871)

![Gobierno Provisional 1870 — anverso](/coins/historica-gob-provisional-1870-anverso.png)
![Gobierno Provisional 1870 — reverso](/coins/historica-gob-provisional-1870-reverso.png)

- **Identificación**: figura femenina recostada (alegoría de Hispania) con corona mural. Sin retrato real — es el periodo entre la deposición de Isabel II y la llegada de Amadeo I
- **Leyenda**: "ESPAÑA" con año (1869, 1870)
- **Material**: plata 900‰ en las de 1, 2 y 5 pesetas
- **Dato clave**: estas monedas establecieron el **sistema de peseta** que duraría 130 años
- **Valor**: piezas de 5 pesetas (duros) en plata pueden alcanzar 50-500€ según conservación

## Alfonso XII (1874-1885)

- Retrato masculino, primero imberbe (joven) y luego con bigote
- Leyenda "ALFONSO XII REY DE ESPAÑA"
- Los duros de plata de este periodo son los más comunes y accesibles para coleccionistas principiantes
- Monedas de oro (25 pesetas, "alfonsinas") son piezas muy cotizadas con prima numismática significativa

## Alfonso XIII (1886-1931)

![Alfonso XIII "pelón" 1889 — anverso](/coins/historica-alfonsoXIII-pelon-anverso.png)
![Alfonso XIII "pelón" 1889 — reverso](/coins/historica-alfonsoXIII-pelon-reverso.png)

- **Identificación**: el retrato **evoluciona desde bebé hasta adulto** porque reinó desde su nacimiento. Los coleccionistas distinguen cinco tipos:
  1. **"Pelón"** (1888-1892): niño sin pelo, las más escasas
  2. **"Bucles"** (1892-1894): niño con rizos
  3. **"Rizos"** (1894-1902): adolescente
  4. **"Cadete"** (1903-1905): joven con uniforme militar
  5. **"Adulto"** (1910-1930): con bigote

- Las piezas "pelón" en buena conservación pueden superar los 1.000€

## II República (1931-1939)

- Hispania de pie con gorro frigio, leyenda "REPÚBLICA ESPAÑOLA"
- Monedas acuñadas en circunstancias bélicas, a menudo con defectos
- Valor numismático alto por contexto histórico y escasez

## Moneda romana, medieval y andalusí

![Moneda romana (época de Augusto) — anverso](/coins/historica-romana-anverso.png)
![Moneda romana — reverso](/coins/historica-romana-reverso.png)

- **Identificación**: pátina verde o gris gruesa, posible tierra adherida. Inscripciones en latín, árabe o caracteres medievales
- **Origen**: frecuentemente hallazgos arqueológicos (campos, ríos, yacimientos)
- **IMPORTANTE**: estas piezas están **absolutamente protegidas** por la Ley de Patrimonio Histórico. Además:
  - Su hallazgo debe comunicarse a la autoridad competente (Comunidad Autónoma)
  - Su venta está sujeta a declaración y puede requerir autorización
  - Su exportación requiere permiso del Ministerio de Cultura
  - Su destrucción es **delito** del art. 323 CP sin discusión

**¿Qué hacer si te llega una moneda antigua?** Derivar al cliente a un numismático profesional. Nunca tasarla "a ojo", nunca proponer fundirla, y nunca comprarla sin documentación que acredite su procedencia legal.

## Tabla resumen de protección legal

| Periodo | Antigüedad | ¿Protegida por ley? | ¿Se puede fundir? |
|---------|-----------|---------------------|-------------------|
| Euros (1999-hoy) | <30 años | Curso legal vigente | NO (infracción) |
| Pesetas (1869-2001) | 25-155 años | Parcialmente (>100 años) | NO recomendado |
| Isabel II - Alfonso XIII | >100 años | Sí (Ley 16/1985) | **NO (posible delito)** |
| Romana / medieval | >500 años | Sí (patrimonio arqueológico) | **NO (delito art. 323 CP)** |`,
      keyPoints: [
        'La Ley 16/1985 del Patrimonio Histórico protege las monedas de más de 100 años.',
        'Destruir moneda histórica protegida puede ser delito del art. 323 del Código Penal (6 meses a 3 años de prisión).',
        'Las monedas de Alfonso XIII "pelón" (1888-1892) son las más escasas y valiosas de su reinado.',
        'Las monedas romanas y medievales son patrimonio arqueológico — su hallazgo debe comunicarse a la autoridad.',
        'Ante cualquier moneda antigua: derivar a un numismático profesional antes de actuar.',
      ],
    },
    {
      slug: 'moneda-extranjera-valor-superior',
      number: '7.4',
      title: 'Moneda extranjera con valor superior al peso',
      durationMinutes: 15,
      videoUrl: null,
      content: `Existe una categoría especial de monedas que sí contienen oro o plata real, pero cuyo valor de mercado supera significativamente el valor de su contenido en metal. **Fundirlas es legal pero económicamente absurdo**.

## El soberano británico

![Soberano británico — anverso (Elizabeth II)](/coins/extranjera-soberano-anverso.png)
![Soberano británico — reverso (San Jorge y el dragón)](/coins/extranjera-soberano-reverso.png)

El **soberano** (*sovereign*) es probablemente la moneda de oro más reconocida del mundo, acuñada desde 1817 por la Royal Mint del Reino Unido.

**Especificaciones**:
- **Oro**: 916,7‰ (22 quilates)
- **Peso**: 7,988 g (7,322 g de oro fino)
- **Diámetro**: 22,05 mm
- **Reverso**: la icónica escena de San Jorge a caballo matando al dragón, diseñada por Benedetto Pistrucci en 1817
- **Anverso**: efigie del monarca reinante (desde Isabel II hasta Carlos III actualmente)

**Valor del metal vs valor de mercado**:

| Concepto | Valor aproximado |
|----------|-----------------|
| Oro contenido (7,32 g × ~95€/g) | ~695€ |
| Precio de mercado (soberano moderno) | 750-850€ |
| Soberano Victoria "escudo" (1838-1887) | 800-3.000€ |
| Soberano Jorge III (1817-1820) | 2.000-15.000€ |
| Medio soberano, cuarto de soberano | Prima aún mayor |

La **prima sobre el contenido en oro** oscila entre el 5% (piezas modernas de inversión) y el 2.000% (rarezas históricas). Incluso un soberano moderno comprado como inversión vale más intacto que fundido.

**¿Por qué se compran soberanos en vez de lingotes?**

1. **Exención de CGT en Reino Unido**: los soberanos están exentos del impuesto sobre ganancias de capital (*Capital Gains Tax*) en el Reino Unido, lo que los hace fiscalmente superiores a los lingotes para inversores británicos
2. **Divisibilidad**: con ~700-800€ por pieza, permiten invertir cantidades más pequeñas que un lingote de 1 oz (~2.800€) o 100 g (~9.500€)
3. **Reconocimiento universal**: se compran y venden instantáneamente en cualquier tienda numismática del mundo
4. **Liquidez**: mercado secundario muy activo; se venden en horas

## Otras monedas de oro con prima numismática

Además del soberano, existen otras monedas bullion que acumulan prima sobre su contenido metálico:

- **Krugerrand sudafricano** (1 oz, 916,7‰): la primera moneda bullion moderna (1967). Piezas de los primeros años tienen prima del 10-30%
- **Eagle americano** (1 oz, 916,7‰): acuñada desde 1986. Modelos "tipo I" (hasta 2021) empiezan a cotizar con prima
- **Maple Leaf canadiense** (1 oz, 999,9‰): la más pura de las bullion estándar
- **Filarmónica de Viena** (1 oz, 999,9‰): la bullion europea por excelencia
- **Panda chino**: cambia de diseño cada año, lo que genera coleccionismo activo y primas del 20-100% en años antiguos

## La regla de oro (nunca mejor dicho)

**Antes de fundir cualquier moneda de oro o plata, pregúntate**:

1. ¿Tiene más de 50 años? → Consulta a un numismático
2. ¿Es de un país extranjero? → Probablemente tiene prima de colección
3. ¿Tiene un diseño diferente al estándar? → Puede ser una variante valiosa
4. ¿Está en buen estado de conservación? → La prima numismática se multiplica con la calidad

**En caso de duda, NO fundir**. El coste de una consulta numismática profesional (0-30€) es insignificante comparado con el valor que podrías destruir.`,
      keyPoints: [
        'El soberano británico contiene 7,32g de oro fino pero su valor de mercado supera siempre al del metal.',
        'Soberanos antiguos (Victoria, Jorge III) pueden valer 10-20 veces su contenido en oro.',
        'Monedas bullion como Krugerrand, Eagle, Maple Leaf tienen prima de colección creciente con los años.',
        'El Panda chino cambia de diseño anualmente, generando primas numismáticas del 20-100%.',
        'Regla práctica: antes de fundir cualquier moneda, consultar siempre a un numismático profesional.',
      ],
    },
  ],
  quiz: [
    {
      id: 'm7-q1',
      question:
        '¿Qué ley española protege las monedas de más de 100 años de antigüedad?',
      options: [
        'Ley de Propiedad Intelectual',
        'Ley 16/1985 del Patrimonio Histórico Español',
        'Ley de Consumidores y Usuarios',
        'Ley del Mercado de Valores',
      ],
      correctIndex: 1,
      explanation:
        'La Ley 16/1985 del Patrimonio Histórico Español protege bienes de interés cultural, incluyendo monedas históricas. Su destrucción puede ser delito del art. 323 del Código Penal.',
    },
    {
      id: 'm7-q2',
      question:
        'Un "duro" de plata de Alfonso XII (5 pesetas, 1878) contiene 22,5g de plata fina. ¿Cuál es la mejor decisión?',
      options: [
        'Fundirlo para recuperar la plata (~20€)',
        'Consultar su valor numismático antes de actuar (puede valer 30-5.000€)',
        'Depositarlo en el Banco de España',
        'Usarlo como moneda de curso legal',
      ],
      correctIndex: 1,
      explanation:
        'El valor numismático de un duro de plata casi siempre supera al de su contenido en metal. Años escasos o piezas en buena conservación pueden multiplicar por 10-250 el valor de la plata contenida.',
    },
    {
      id: 'm7-q3',
      question:
        '¿Por qué NO se deben fundir las monedas de 2€ conmemorativos?',
      options: [
        'Porque contienen oro',
        'Porque son moneda de curso legal y su destrucción es infracción, además de que el metal vale menos de 2€',
        'Porque están protegidas por la Ley de Patrimonio Histórico',
        'Porque pertenecen al Banco Central Europeo',
      ],
      correctIndex: 1,
      explanation:
        'Las monedas de 2€ conmemorativos son moneda de curso legal con valor facial de 2€. Están hechas de cuproníquel y latón-níquel, metales que valen mucho menos que 2€. Destruirlas es infracción y económicamente absurdo.',
    },
    {
      id: 'm7-q4',
      question:
        '¿Cómo se identifica un soberano británico auténtico?',
      options: [
        'Por su color plateado y el escudo real',
        'Por el reverso con San Jorge matando al dragón, peso de 7,988g y oro de 22 quilates',
        'Por tener inscripción "SOBERANO" en el canto',
        'Por el retrato de la reina Victoria exclusivamente',
      ],
      correctIndex: 1,
      explanation:
        'El soberano se identifica por su reverso icónico (San Jorge y el dragón), su peso exacto de 7,988g y su ley de 916,7‰ (22 quilates). El anverso muestra al monarca reinante en el momento de la acuñación, no exclusivamente a Victoria.',
    },
    {
      id: 'm7-q5',
      question:
        '¿Qué artículo del Código Penal español sanciona la destrucción de monedas históricas protegidas?',
      options: [
        'Artículo 234 (hurto)',
        'Artículo 323 (daños al patrimonio histórico)',
        'Artículo 248 (estafa)',
        'Artículo 390 (falsificación de moneda)',
      ],
      correctIndex: 1,
      explanation:
        'El artículo 323 del Código Penal castiga con 6 meses a 3 años de prisión o multa a quien cause daños en bienes de valor histórico, artístico, científico, cultural o monumental. Fundir una moneda protegida por la Ley 16/1985 encaja en este tipo.',
    },
  ],
};
